/**
 * RuleGroupEditor.js - 客户细分「嵌套条件组」递归编辑器
 * 每个分组含 AND/OR 切换，子节点可为条件行或子分组（缩进渲染）
 */
import React from 'react';
import { Select, Input, Button, Row, Col, Space, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { emptyCondition, emptyGroup, treeMaxDepth, MAX_RULE_DEPTH } from '../../../utils/segmentRules';

const { Option } = Select;

const NUMBER_OPERATORS = [
  { label: '等于', value: 'eq' }, { label: '不等于', value: 'neq' },
  { label: '大于', value: 'gt' }, { label: '大于等于', value: 'gte' },
  { label: '小于', value: 'lt' }, { label: '小于等于', value: 'lte' },
];
const STRING_OPERATORS = [
  { label: '等于', value: 'eq' }, { label: '不等于', value: 'neq' },
  { label: '包含', value: 'contains' }, { label: '开头是', value: 'startsWith' },
  { label: '结尾是', value: 'endsWith' },
];
const TAG_OPERATORS = [
  { label: '包含此标签', value: 'contains' }, { label: '不包含此标签', value: 'not_contains' },
  { label: '等于此标签', value: 'eq' }, { label: '不等于此标签', value: 'neq' },
];

function isGroupNode(n) {
  return n && (n.type === 'group' || Array.isArray(n.children));
}

export default function RuleGroupEditor({ node, onChange, onRemove, depth = 0, fields, tags }) {
  // 更新第 i 个子节点
  const updateChild = (i, newChild) => {
    const children = node.children.map((c, idx) => (idx === i ? newChild : c));
    onChange({ ...node, children });
  };
  // 删除第 i 个子节点（至少保留一个）
  const removeChild = (i) => {
    if (node.children.length <= 1) return;
    onChange({ ...node, children: node.children.filter((_, idx) => idx !== i) });
  };
  const addCondition = () => onChange({ ...node, children: [...node.children, emptyCondition()] });
  const addGroup = () => onChange({ ...node, children: [...node.children, emptyGroup('AND')] });

  // 当前整棵的深度守卫：若再加子组会超过上限则禁用
  const canAddGroup = depth + 2 <= MAX_RULE_DEPTH;

  const groupBorder = depth === 0 ? '1px solid #d9d9d9' : '1px dashed #bfbfbf';

  return (
    <div style={{ border: groupBorder, borderRadius: 6, padding: 12, marginBottom: 8, background: depth % 2 ? '#fafafa' : '#fff' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <Space>
            <span>组内逻辑</span>
            <Select
              size="small"
              value={node.logic}
              style={{ width: 150 }}
              onChange={(logic) => onChange({ ...node, logic })}
            >
              <Option value="AND">满足所有 (且)</Option>
              <Option value="OR">满足任一 (或)</Option>
            </Select>
          </Space>
        </Col>
        <Col>
          {onRemove && (
            <Button size="small" danger icon={<DeleteOutlined />} onClick={onRemove}>删除本组</Button>
          )}
        </Col>
      </Row>

      {node.children.map((child, i) => {
        if (isGroupNode(child)) {
          return (
            <div key={child.id || i} style={{ marginLeft: 16 }}>
              <RuleGroupEditor
                node={child}
                depth={depth + 1}
                fields={fields}
                tags={tags}
                onChange={(nc) => updateChild(i, nc)}
                onRemove={() => removeChild(i)}
              />
            </div>
          );
        }
        // 条件行
        const fieldConfig = fields.find((f) => f.value === child.field);
        const isTag = fieldConfig?.type === 'tag';
        const operators = isTag ? TAG_OPERATORS : (fieldConfig?.type === 'number' ? NUMBER_OPERATORS : STRING_OPERATORS);
        const setField = (k, v) => {
          if (k === 'field') {
            const cfg = fields.find((f) => f.value === v);
            const ops = cfg?.type === 'tag'
              ? TAG_OPERATORS
              : (cfg?.type === 'number' ? NUMBER_OPERATORS : STRING_OPERATORS);
            // 字段类型变化时，重置操作符为新类型的首个选项并清空值，避免保存非法组合
            updateChild(i, { ...child, field: v, operator: ops[0].value, value: '' });
          } else {
            updateChild(i, { ...child, [k]: v });
          }
        };
        return (
          <Row key={child.id || i} gutter={8} style={{ marginBottom: 8, marginLeft: 16 }} align="middle">
            <Col span={7}>
              <Select value={child.field} style={{ width: '100%' }}
                onChange={(v) => setField('field', v)}>
                {fields.map((f) => <Option key={f.value} value={f.value}>{f.label}</Option>)}
              </Select>
            </Col>
            <Col span={5}>
              <Select value={child.operator} style={{ width: '100%' }}
                onChange={(v) => setField('operator', v)}>
                {operators.map((op) => <Option key={op.value} value={op.value}>{op.label}</Option>)}
              </Select>
            </Col>
            <Col span={8}>
              {isTag ? (
                <Select value={child.value} style={{ width: '100%' }} placeholder="选择标签" allowClear
                  onChange={(v) => setField('value', v)}>
                  {tags.map((t) => <Option key={t.id} value={t.name}>{t.name}</Option>)}
                </Select>
              ) : (
                <Input value={child.value}
                  placeholder={fieldConfig?.type === 'number' ? '数值' : '文本'}
                  onChange={(e) => setField('value', e.target.value)} />
              )}
            </Col>
            <Col span={4}>
              <Button size="small" danger icon={<DeleteOutlined />}
                disabled={node.children.length <= 1}
                onClick={() => removeChild(i)} style={{ width: '100%' }} />
            </Col>
          </Row>
        );
      })}

      <Space style={{ marginLeft: 16, marginTop: 4 }}>
        <Button size="small" icon={<PlusOutlined />} onClick={addCondition}>添加条件</Button>
        <Button size="small" icon={<PlusOutlined />} onClick={addGroup} disabled={!canAddGroup}>
          添加条件组{!canAddGroup && `（最多${MAX_RULE_DEPTH}层）`}
        </Button>
      </Space>
      {depth === 0 && treeMaxDepth(node) >= MAX_RULE_DEPTH && (
        <div style={{ marginLeft: 16, marginTop: 6 }}>
          <Tag color="orange">已达最大嵌套深度 {MAX_RULE_DEPTH} 层</Tag>
        </div>
      )}
    </div>
  );
}
